import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import { useToast } from "../hooks/useToast";
import { getVehicleById, getTransactionsByVehicle } from "../services/api";

interface Vehicle {
  id: number;
  vehicleNo: string;
  ownerName: string;
  ownerPhone: string;
  vehicleType: string;
  balance: number;
  monthlyFee: number;
}

interface TransactionItem {
  id: number;
  transactionType: string;
  reason: string;
  amount: number;
  createdAt: string;
}

const VehicleDetailsImproved: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [transactions, setTransactions] = useState<TransactionItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { showToast, ToastContainer } = useToast();

  useEffect(() => {
    if (id) {
      fetchVehicleDetails();
      fetchVehicleTransactions();
    }
    // eslint-disable-next-line
  }, [id]);

  const fetchVehicleDetails = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getVehicleById(id!);
      setVehicle(data);
      showToast("Vehicle details fetched", "success");
    } catch (err: any) {
      setError(err?.message || "Failed to fetch vehicle details");
      showToast(err?.message || "Failed to fetch vehicle details", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicleTransactions = async () => {
    setLoading(true);
    setError("");
    try {
      const vehicleId = Number(id);
      const trans: TransactionItem[] = await getTransactionsByVehicle(vehicleId);
      trans.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      setTransactions(trans);
      showToast("Vehicle transactions fetched", "success");
    } catch (err: any) {
      setError(err?.message || "Failed to fetch transactions");
      showToast(err?.message || "Failed to fetch transactions", "error");
    } finally {
      setLoading(false);
    }
  };

  if (!vehicle) {
    return (
      <div className="min-h-screen w-full bg-gray-50 flex flex-col">
        <ToastContainer />
        <Card className="max-w-4xl mx-auto p-6">
          <div className="flex items-center mb-4">
            <Button color="secondary" onClick={() => navigate(-1)} className="mr-4">Back</Button>
            <h1 className="text-2xl font-bold text-blue-700">Vehicle Details</h1>
          </div>
          {loading ? <LoadingSpinner /> : <div className="text-gray-500">Loading vehicle details...</div>}
          {error && <div className="text-red-600 mb-2">{error}</div>}
        </Card>
      </div>
    );
  }

  return (
  <div className="min-h-screen w-screen bg-gray-50 flex flex-col">
      <ToastContainer />
  <Card className="w-full mx-auto p-4 md:p-6">
        <div className="flex items-center mb-4">
          <Button color="secondary" onClick={() => navigate(-1)} className="mr-4">Back</Button>
          <h1 className="text-2xl font-bold text-blue-700">Vehicle Details</h1>
        </div>
        {loading && <LoadingSpinner />}
        {error && <div className="text-red-600 mb-2">{error}</div>}
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-2">Vehicle Info</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <p><span className="font-semibold text-gray-600">Vehicle No:</span> {vehicle.vehicleNo}</p>
            <p><span className="font-semibold text-gray-600">Type:</span> {vehicle.vehicleType}</p>
            <p><span className="font-semibold text-gray-600">Owner:</span> {vehicle.ownerName}</p>
            <p><span className="font-semibold text-gray-600">Phone:</span> {vehicle.ownerPhone}</p>
            <p><span className="font-semibold text-gray-600">Balance:</span> ₹{vehicle.balance}</p>
            <p><span className="font-semibold text-gray-600">Monthly Fee:</span> ₹{vehicle.monthlyFee}</p>
          </div>
        </div>
        <div className="flex flex-col md:flex-row gap-3 w-full mb-6">
          <Button
            style={{ background: "#22c55e", color: "#fff" }}
            className="flex-1 py-3 rounded-lg text-base font-semibold shadow hover:bg-green-700 transition"
            onClick={() => navigate(`/payments/collect/${vehicle.id}`)}
          >
            Collect Payment
          </Button>
          <Button
            style={{ background: "#2563eb", color: "#fff" }}
            className="flex-1 py-3 rounded-lg text-base font-semibold shadow hover:bg-blue-700 transition"
            onClick={() => navigate(`/create-invoice/${vehicle.id}`)}
          >
            Raise Invoice
          </Button>
          <Button
            style={{ background: "#ef4444", color: "#fff" }}
            className="flex-1 py-3 rounded-lg text-base font-semibold shadow hover:bg-red-700 transition"
            onClick={() => navigate(`/writeoffs/create/${vehicle.id}`)}
          >
            Write Off
          </Button>
        </div>
        <div>
          <h2 className="text-lg font-semibold mb-2">Recent Transactions</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white shadow rounded text-sm md:text-base">
              <thead className="hidden md:table-header-group">
                <tr className="bg-gray-100 text-left">
                  <th className="py-2 px-4">ID</th>
                  <th className="py-2 px-4">Transaction Type</th>
                  <th className="py-2 px-4">Reason</th>
                  <th className="py-2 px-4">Amount (₹)</th>
                  <th className="py-2 px-4">Date & Time</th>
                </tr>
              </thead>
              <tbody>
                {transactions.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-4 text-gray-500">
                      No transactions found
                    </td>
                  </tr>
                ) : (
                  transactions.map((t) => (
                    <tr
                      key={t.id}
                      className="border-b md:table-row flex flex-col md:flex-row mb-4 md:mb-0 p-4 md:p-0 rounded md:rounded-none hover:bg-blue-50 transition"
                    >
                      <td className="py-2 px-0 md:px-4">
                        <span className="md:hidden text-gray-500">ID: </span>
                        {t.id}
                      </td>
                      <td className="py-2 px-0 md:px-4">
                        <span className="md:hidden text-gray-500">Type: </span>
                        {t.transactionType}
                      </td>
                      <td className="py-2 px-0 md:px-4">
                        <span className="md:hidden text-gray-500">Reason: </span>
                        {t.reason}
                      </td>
                      <td className="py-2 px-0 md:px-4">
                        <span className="md:hidden text-gray-500">Amount: </span>
                        ₹{t.amount}
                      </td>
                      <td className="py-2 px-0 md:px-4">
                        <span className="md:hidden text-gray-500">Date: </span>
                        {new Date(t.createdAt).toLocaleString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default VehicleDetailsImproved;
